import React, { useEffect, useState } from "react";
import { Loader2, Radar, Play, Trash2, Plus, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const FRAMEWORK_CHOICES = ["GDPR", "CCPA", "HIPAA", "SOC2", "PCI-DSS", "FTC"];

export default function Monitoring() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/monitoring/jobs"); setJobs(data); }
    catch (err) { toast.error("Couldn't load monitors", { description: formatApiError(err) }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runNow = async (job) => {
    toast.info("Running monitor…");
    try {
      await api.post(`/monitoring/jobs/${job.id}/run`);
      toast.success("Monitor run complete — check notifications");
      load();
    } catch (err) { toast.error("Run failed", { description: formatApiError(err) }); }
  };
  const remove = async (job) => {
    try {
      await api.delete(`/monitoring/jobs/${job.id}`);
      setJobs((js) => js.filter((j) => j.id !== job.id));
    } catch (err) { toast.error("Delete failed", { description: formatApiError(err) }); }
  };

  return (
    <div className="space-y-8" data-testid="monitoring-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600">Monitoring</p>
          <h1 className="mt-1.5 font-display text-3xl font-semibold tracking-tight text-slate-900">
            Autonomous regulation watchers
          </h1>
          <p className="mt-1.5 text-sm text-slate-600">
            Schedule AI agents to watch GDPR, CCPA and other frameworks and ping you when something material changes.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white" data-testid="monitoring-new">
              <Plus className="h-4 w-4 mr-2" /> New monitor
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <CreateMonitorForm onCreated={() => { setOpen(false); load(); }} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {loading && <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div>}
        {!loading && jobs.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="monitoring-empty">
            <Radar className="h-8 w-8 mx-auto text-slate-400" />
            <h3 className="mt-3 font-display text-lg font-semibold text-slate-900">No monitors yet</h3>
            <p className="mt-1 text-sm text-slate-600">Create a daily or weekly agent to track regulatory updates.</p>
          </div>
        )}
        {jobs.map((j) => (
          <div key={j.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`monitor-${j.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-display text-lg font-semibold text-slate-900">{j.name}</div>
                  <span className="rounded-full bg-blue-50 text-blue-700 text-[11px] font-semibold px-2 py-0.5 capitalize">{j.cadence}</span>
                  {!j.enabled && <span className="rounded-full bg-slate-100 text-slate-600 text-[11px] px-2 py-0.5">Paused</span>}
                </div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {(j.frameworks || []).map((f) => (
                    <span key={f} className="rounded-full bg-slate-100 text-slate-700 text-[11px] px-2 py-0.5 border border-slate-200">{f}</span>
                  ))}
                </div>
                {j.last_summary && (
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed max-w-2xl">{j.last_summary}</p>
                )}
                <div className="mt-3 text-xs text-slate-500 flex flex-wrap gap-3">
                  {j.last_run_at && <span>Last run: {new Date(j.last_run_at).toLocaleString()}</span>}
                  {j.next_run_at && <span>Next: {new Date(j.next_run_at).toLocaleString()}</span>}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button onClick={() => runNow(j)} className="rounded-md bg-blue-600 hover:bg-blue-700 text-white h-9" data-testid={`monitor-run-${j.id}`}>
                  <Play className="h-3.5 w-3.5 mr-1.5" /> Run now
                </Button>
                <Button onClick={() => remove(j)} variant="ghost" className="rounded-md text-slate-500 hover:text-rose-600 h-9 w-9 p-0">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CreateMonitorForm({ onCreated }) {
  const [name, setName] = useState("Weekly GDPR / CCPA digest");
  const [cadence, setCadence] = useState("weekly");
  const [frameworks, setFrameworks] = useState(["GDPR", "CCPA"]);
  const [keywords, setKeywords] = useState("");
  const [busy, setBusy] = useState(false);

  const toggle = (f) =>
    setFrameworks((cur) => cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/monitoring/jobs", {
        name, cadence, frameworks,
        keywords: keywords.split(",").map((k) => k.trim()).filter(Boolean),
        enabled: true,
      });
      toast.success("Monitor created");
      onCreated();
    } catch (err) { toast.error("Couldn't create monitor", { description: formatApiError(err) }); }
    finally { setBusy(false); }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>New monitoring agent</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 pt-2">
        <div>
          <Label>Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-10" data-testid="monitor-name" />
        </div>
        <div>
          <Label>Cadence</Label>
          <Select value={cadence} onValueChange={setCadence}>
            <SelectTrigger className="mt-1 h-10" data-testid="monitor-cadence">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Frameworks</Label>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {FRAMEWORK_CHOICES.map((f) => {
              const active = frameworks.includes(f);
              return (
                <button
                  key={f} type="button" onClick={() => toggle(f)}
                  className={"rounded-full text-xs font-medium px-3 py-1 border transition-colors " +
                    (active ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50")}
                  data-testid={`monitor-framework-${f}`}
                >{f}</button>
              );
            })}
          </div>
        </div>
        <div>
          <Label>Keywords (optional, comma-separated)</Label>
          <Input value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="ai disclosure, ePrivacy, third-party data" className="mt-1 h-10" data-testid="monitor-keywords" />
        </div>
      </div>
      <DialogFooter className="mt-2">
        <Button onClick={submit} disabled={busy} className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white" data-testid="monitor-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create monitor"}
        </Button>
      </DialogFooter>
    </>
  );
}
